import React, { useCallback, useEffect, useState } from "react";
import { Box, Text, Button, Grid, Label, Flex, Modal, ErrorMsg } from "atoms";
import { FormInput, FormSelect } from "molecules";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import MemoClose from "assets/icons/Close";
import { InvitationSentModal } from "../InvitationSentModal";

const validationSchema = Yup.object().shape({
  role: Yup.string().required("Role is required"),
  name: Yup.string().required("Name is required"),
  email: Yup.string().email().required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .min(7, "Password too short")
    .max(20, "Password too long")
    .matches(/^(?=.*[a-z])/, "Must contain at least one lowercase character")
    .matches(/^(?=.*[A-Z])/, "Must contain at least one uppercase character")
    .matches(/^(?=.*[0-9])/, "Must contain at least one number")
    .matches(
      /^(?=.*[!@#$%^&*?~`])/,
      "Must contain at least one special character"
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords don't match")
    .required("Confirm Password is required"),
});

export const AddNewUserModal = ({
  isOpen,
  onClose,
  preUser,
  onSubmit,
  loading,
  getSelectRoleOptions,
  roleOptions,
  error,
  resetError,
}) => {
  const [isConfirmModalVisible, setConfirmModalVisible] = useState(false);

  const [rolesLoading, setRolesLoading] = useState(false);
  const loadRoles = useCallback(() => {
    setRolesLoading((prev) => true);
    getSelectRoleOptions()
      ?.then(() => {})
      .finally(() => {
        setRolesLoading(false);
      });
  }, [getSelectRoleOptions]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <Box width={{ md: "48.8rem" }}>
          <Box p={{ xs: "2.4rem", lg: "3.2rem" }}>
            <Flex
              justifyContent="space-between"
              borderBottom="0.1rem solid"
              borderColor="neutral.100"
              pb="2.4rem"
            >
              <Text
                fontSize="2rem"
                color="primary.900"
                fontFamily="primaryBold"
              >
                {preUser ? "Edit" : "Add New"} User
              </Text>
              <Box
                color="neutral.900"
                onClick={onClose}
                cursor="pointer"
                textAlign="right"
              >
                <MemoClose height="1.6rem" width="1.6rem" />
              </Box>
            </Flex>
            <Formik
              initialValues={{
                role: preUser?.roles?.name || "",
                name: preUser?.name || "",
                email: preUser?.email || "",
                password: "",
                confirmPassword: "",
              }}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
            >
              {({ values, setFieldValue }) => {
                return (
                  <Form>
                    <Grid gridRowGap="2.4rem" mt="1.8rem">
                      <Grid gridRowGap="1.6rem">
                        <Label text="Role" req />
                        <FormSelect
                          name="role"
                          placeholder="Enter Role"
                          req
                          onChange={(e) => {
                            // setFieldValue("role", e?.target?.value);
                            error && resetError();
                          }}
                          isLoading={rolesLoading}
                          options={roleOptions?.map((role) => ({
                            label: role?.name,
                            value: role?.name,
                          }))}
                        />
                      </Grid>
                      <Grid gridRowGap="1.6rem">
                        <Label text="Full Name" req />
                        <FormInput
                          name="name"
                          type="name"
                          placeholder="Enter Full Name"
                          req
                          onChange={(e) => {
                            setFieldValue("name", e?.target?.value);
                            error && resetError();
                          }}
                        />
                      </Grid>
                      <Grid gridRowGap="1.6rem">
                        <Label text="Email" req />
                        <FormInput
                          name="email"
                          type="email"
                          placeholder="Enter Email"
                          req
                          onChange={(e) => {
                            setFieldValue("email", e?.target?.value);
                            error && resetError();
                          }}
                        />
                      </Grid>
                      {!preUser && (
                        <>
                          <Grid gridRowGap="1.6rem">
                            <Label text="Password" req />
                            <FormInput
                              name="password"
                              type="password"
                              placeholder="Enter Password"
                              req
                              onChange={(e) => {
                                setFieldValue("password", e?.target?.value);
                                error && resetError();
                              }}
                            />
                          </Grid>
                          <Grid gridRowGap="1.6rem">
                            <Label text="Confirm Password" req />
                            <FormInput
                              name="confirmPassword"
                              type="password"
                              placeholder="Enter Confirm Password"
                              req
                              onChange={(e) => {
                                setFieldValue(
                                  "confirmPassword",
                                  e?.target?.value
                                );
                                error && resetError();
                              }}
                            />
                          </Grid>
                        </>
                      )}

                      {error && <ErrorMsg text={error} />}
                      <Button
                        variant="primary"
                        type="submit"
                        height={{ xs: "4.3rem", lg: "4.8rem" }}
                        loading={loading}
                      >
                       Save
                      </Button>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </Box>
      </Modal>
      {isConfirmModalVisible && (
        <InvitationSentModal
          isOpen={isConfirmModalVisible}
          onClose={() => setConfirmModalVisible(false)}
        />
      )}
    </>
  );
};
